import { useState, useEffect, useRef } from "react";

/**
 * Custom hook to observe and return an element's dimensions when it resizes
 * @returns Object containing the ref to attach to an element and its current dimensions
 */
export function useResizeObserver<T extends HTMLElement>() {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Create a new ResizeObserver instance
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        const { width, height } = entries[0].contentRect;
        setDimensions({ width, height });
      }
    });

    // Start observing the element
    resizeObserver.observe(element);

    // Clean up the observer when component unmounts
    return () => {
      resizeObserver.unobserve(element);
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, width: dimensions.width, height: dimensions.height };
}
